/**
 * Expense panel — top-right quadrant.
 * Shows all expense categories with amounts and mini bars.
 */

import { useState } from 'react';
import type { MonthlySummary } from '../../types/cashflow';
import { formatCurrency } from '../../utils/cashflowCalc';

interface Props {
  summary: MonthlySummary;
}

export default function ExpensePanel({ summary }: Props) {
  const [collapsed, setCollapsed] = useState(false);

  const rows = [
    { label: 'Taxes', amount: summary.tax_expenses },
    { label: 'Housing', amount: summary.housing_expenses },
    { label: 'Living', amount: summary.living_expenses },
    { label: 'Debt Service', amount: summary.debt_expenses },
    { label: 'Other', amount: summary.other_expenses },
  ];

  const totalExpenses = rows.reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="card" style={{ flex: 1, minWidth: 280 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)', cursor: 'pointer' }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <h3 style={{ color: 'var(--color-error)', margin: 0, fontSize: '1rem', fontWeight: 700 }}>
          ↓ EXPENSES
        </h3>
        <span style={{ color: 'var(--color-text-secondary)', fontSize: '1.1rem' }}>
          {formatCurrency(totalExpenses)} {collapsed ? '▸' : '▾'}
        </span>
      </div>

      {!collapsed && (
        <div>
          {rows.map((row) => (
            <ExpenseRow key={row.label} label={row.label} amount={row.amount} total={totalExpenses} />
          ))}
          <div style={{ borderTop: '2px solid var(--color-border)', paddingTop: 'var(--spacing-sm)', marginTop: 'var(--spacing-sm)', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <span>Total</span>
            <span style={{ color: 'var(--color-error)' }}>{formatCurrency(totalExpenses)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ExpenseRow({ label, amount, total }: { label: string; amount: number; total: number }) {
  const pct = total > 0 ? (amount / total) * 100 : 0;
  return (
    <div style={{ marginBottom: 'var(--spacing-md)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{label}</span>
        <span style={{ fontWeight: 600 }}>{formatCurrency(amount)}</span>
      </div>
      <div style={{ height: 4, background: 'var(--color-border)', borderRadius: 2 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--color-error)', borderRadius: 2, opacity: 0.7, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}
